import React, { useEffect, useState } from "react";
import { Target, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SEEN_KEY = "gv_milestones_seen_v1";
const fmt = (n, prefix = "") => `${prefix}${Number(n || 0) >= 1000 ? (Number(n) / 1000).toFixed(Number(n) % 1000 === 0 ? 0 : 1) + "k" : Number(n || 0).toLocaleString()}`;

export default function NextMilestone() {
  const [ms, setMs] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/growth/milestones").then((r) => {
      if (!alive) return;
      const list = r.data?.milestones || [];
      setMs(list);
      // Celebrate any milestone crossed since we last looked (deduped per metric/threshold).
      let seen = {};
      try { seen = JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { seen = {}; }
      let changed = false;
      list.forEach((m) => {
        if (m.last_reached != null && seen[m.metric] !== m.last_reached) {
          if (seen[m.metric] != null) {
            toast.success(`🎉 Milestone unlocked: ${fmt(m.last_reached, m.prefix)} ${m.label}!`, { duration: 6000 });
          }
          seen[m.metric] = m.last_reached; changed = true;
        }
      });
      if (changed) { try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch {} }
    }).catch(() => setMs([]));
    return () => { alive = false; };
  }, []);

  if (!ms || !ms.length) return null;
  const active = ms.filter((m) => m.next != null);
  if (!active.length) return null;

  return (
    <div className="mb-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:p-5" data-testid="next-milestone">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[hsl(var(--primary))]" />
        <h3 className="font-display text-sm font-extrabold">Your next milestones</h3>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Keep the momentum going</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {active.map((m) => (
          <div key={m.metric} data-testid={`milestone-${m.metric}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold capitalize flex items-center gap-1"><span>{m.icon}</span> {m.label}</span>
              <span className="text-[hsl(var(--muted-foreground))]">{fmt(m.current, m.prefix)} / {fmt(m.next, m.prefix)}</span>
            </div>
            <div className="h-2.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] transition-all"
                style={{ width: `${Math.min(100, m.pct)}%` }} data-testid={`milestone-bar-${m.metric}`} />
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-emerald-500" />
              {m.prefix === "$"
                ? `$${Number(m.remaining).toFixed(2)} to go`
                : `${Number(m.remaining).toLocaleString()} more to hit ${fmt(m.next, m.prefix)}`}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
